import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../Checkbox';
import Radio from '../Radio';
import Text from '../Text';
import { st, classes } from './Selector.st.css';
import ExtraText from './ExtraText';
import ProgressBar from './ProgressBar';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import { SelectorProps, SelectorToggleTooltipProps } from './Selector.types';
import Tooltip from '../Tooltip';

const Selector = ({
  dataHook,
  size = 'medium',
  imageSize = 'large',
  imageShape = 'rectangular',
  image,
  toggleType = 'radio',
  showDivider = false,
  title,
  subtitle,
  extraNode,
  isSelected = false,
  isDisabled = false,
  toggleTooltipProps,
  indeterminate,
  showBelowNodeOnSelect,
  subtitleNode,
  belowNode,
  className,
  id,
  onToggle = i => i,
}: SelectorProps) => {
  const onClick = () => !isDisabled && onToggle(id);

  return (
    <WixDesignSystemContext.Consumer>
      {({ newColorsBranding }) => (
        <li
          data-hook={dataHook}
          className={st(
            classes.root,
            { size, newColorsBranding, showDivider },
            className,
          )}
          onClick={onClick}
          data-shape={imageShape}
          data-size={imageSize}
        >
          <div className={st(classes.mainPart, { disabled: isDisabled })}>
            <ConditionalTooltip
              toggleTooltipProps={!!isDisabled ? toggleTooltipProps : undefined}
            >
              {toggleType === 'checkbox' ? (
                <Checkbox
                  dataHook="toggle"
                  checked={isSelected}
                  disabled={isDisabled}
                  indeterminate={indeterminate}
                />
              ) : (
                <Radio
                  dataHook="toggle"
                  checked={isSelected}
                  disabled={isDisabled}
                />
              )}
            </ConditionalTooltip>
            {image && (
              <div
                data-hook="selector-image"
                className={st(classes.image, {
                  imageSize,
                  imageShape,
                })}
                children={image}
              />
            )}

            <div className={classes.titles}>
              <Text
                dataHook="selector-title"
                size={size}
                ellipsis
                children={title}
              />

              {subtitle && (
                <Text
                  size={size === 'medium' ? 'small' : 'tiny'}
                  secondary
                  dataHook="selector-subtitle"
                  ellipsis
                  children={subtitle}
                />
              )}

              {subtitleNode && (
                <div data-hook="subtitle-node">{subtitleNode}</div>
              )}
            </div>

            {extraNode && (
              <div
                className={classes.extra}
                data-hook="selector-extra-node"
                children={extraNode}
              />
            )}
          </div>

          {showBelowNodeOnSelect && belowNode && isSelected && (
            <div data-hook="below-section" className={classes.belowSection}>
              {belowNode}
            </div>
          )}
        </li>
      )}
    </WixDesignSystemContext.Consumer>
  );
};

const ConditionalTooltip = ({
  toggleTooltipProps,
  children,
}: {
  toggleTooltipProps?: SelectorToggleTooltipProps;
  children: React.ReactNode;
}) => {
  return toggleTooltipProps ? (
    <Tooltip
      content={toggleTooltipProps?.content}
      disabled={false}
      dataHook="toggle-tooltip"
      enterDelay={toggleTooltipProps.transitionSettings?.openDelay}
      exitDelay={toggleTooltipProps.transitionSettings?.closeDelay}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};

Selector.propTypes = {
  dataHook: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium']),
  imageSize: PropTypes.oneOf(['tiny', 'small', 'portrait', 'large', 'cinema']),
  imageShape: PropTypes.oneOf(['rectangular', 'circle']),
  showDivider: PropTypes.bool,
  title: PropTypes.node.isRequired,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  subtitle: PropTypes.string,
  extraNode: PropTypes.node,
  onToggle: PropTypes.func,
  toggleType: PropTypes.oneOf(['checkbox', 'radio']),
  showBelowNodeOnSelect: PropTypes.bool,
  belowNode: PropTypes.node,
  toggleTooltipProps: PropTypes.shape({
    content: PropTypes.string.isRequired,
    transitionSettings: PropTypes.shape({
      openDelay: PropTypes.number,
      closeDelay: PropTypes.number,
    }),
  }),
  subtitleNode: PropTypes.node,
  className: PropTypes.string,
};

Selector.displayName = 'Selector';
Selector.ExtraText = ExtraText;
Selector.ProgressBar = ProgressBar;

export default Selector;
